import {
  IonContent,
  IonHeader,
  IonList,
  IonPage,
  IonSpinner,
  IonImg,
  IonIcon,
} from "@ionic/react";
import "@ionic/react/css/ionic-swiper.css";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router";
import "swiper/css";
import MyButtonWithBorder from "../../components/my-button-with-border/my-button-with-border";
import Navbar from "../../components/navbar/navbar";
import { LocalizationContext } from "../../contexts/localization.context";
import { UserStatus } from "../../models/contexts/user.context.model";
import { SelectionContext } from "../../contexts/selection.context";
import { UserContext } from "../../contexts/user.context";
import { ItineraryToken } from "../../models/api/itineraryTokens.models";
import { ItineraryContext } from "../../contexts/itinerary.context";
import {
  ENDPOINT_GET_ITINERARY_TOKENS_WITH_ITINERARY,
  ON_DEV,
} from "../../variables";
import {
  GetItinerariesByCityOutput,
  Itinerary,
} from "../../models/api/itineraries.models";
import { Routes } from "../../routes";
import { get } from "../../services/http-service";
import {
  ENDPOINT_GET_ITINERARIES_FILTERED,
  ENDPOINT_GET_TAGS_BY_CITY,
} from "../../variables";
import styles from "./city-itineraries.module.css";
import {
  GetTagsByCityOutput,
  TagWithLocales,
} from "../../models/api/tags.models";
import { shareOutline } from "ionicons/icons";

// Add this interface
interface TagsMap {
  [key: number]: TagWithLocales;
}

const CityItinerariesPage = () => {
  const userContext = useContext(UserContext);
  const selectionContext = useContext(SelectionContext);
  const localizationContext = useContext(LocalizationContext);
  const itineraryContext = useContext(ItineraryContext);
  const location = useLocation();
  const { t } = useTranslation();
  const history = useHistory();
  const [itineraries, setItineraries] = useState<Itinerary[]>([]);
  const [tags, setTags] = useState<TagsMap>({});
  const [isPending, setPending] = useState<boolean>(false);
  const [fetchError, setFetchError] = useState<boolean>(false);
  const [userItineraryTokens, setUserItineraryTokens] = useState<
    ItineraryToken[]
  >([]);
  const [isTagsLoading, setIsTagsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (location.pathname !== Routes.CITY_ITINERARIES) return;
    fetchItineraries();
    if (userContext.user.status === UserStatus.LOGGED) {
      fetchUserItineraries();
    }
  }, [location.pathname, userContext.user.status, selectionContext.selectedCity]);

  function itineraryButtonClick(itinerary: Itinerary) {
    selectionContext.selectItinerary(itinerary);
    history.push(Routes.SELECTED_ITINERARY_INTRO);
  }

  function playItinerary(itineraryToken: ItineraryToken) {
    itineraryContext.startItinerary(itineraryToken);
  }
  const createSlug = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, "") // Remove emojis
      .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, ""); // Remove leading/trailing hyphens
  };
  const createShareableUrl = (city: string, itinerary: Itinerary) => {
    const baseUrl = "https://zonzo.online/city-itineraries";
    const citySlug = city.toLowerCase();
    const itinerarySlug = createSlug(itinerary.locales[0].name);
    return `${baseUrl}?city=${citySlug}&itinerary=${itinerarySlug}`;
  };

  const handleShare = async (city: string, itinerary: Itinerary) => {
    const shareUrl = createShareableUrl(city, itinerary);
    if (navigator.share) {
      try {
        await navigator.share({
          title: itinerary.locales[0].name,
          text: `Check out this itinerary in ${city} on Zonzo!`,
          url: shareUrl,
        });
      } catch (err) {
        console.error("Error sharing:", err);
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard.writeText(shareUrl);
      // You might want to add a toast notification here to inform the user
    }
  };

  const itineraryItems = itineraries.map((itinerary) => {
    const firstTag = itinerary.tags?.[0];
    const tagData = firstTag ? tags[firstTag.id] : null;
    const tagName = tagData?.locales?.[0]?.name || "";

    return (
      <div className={styles.itineraryCard} key={itinerary.id}>
        <div className={styles.itineraryHeader}>
          <IonImg
            src={`categories/${tagName.split(" ").join("-").toLowerCase()}.png`}
            alt="Category"
            className={styles.imgCategory}
          ></IonImg>
          <button
            className={styles.shareButton}
            onClick={() => handleShare(itinerary.city, itinerary)}
          >
            <IonIcon icon={shareOutline} />
          </button>
        </div>
        <p className={styles.itineraryTitle}>{itinerary.city}</p>
        <h1>{itinerary.locales[0].name}</h1>
        <p
          className={styles.itineraryCardHTML}
          dangerouslySetInnerHTML={{ __html: itinerary.locales[0].description }}
        ></p>
        <p className="itineraryCardFooter">
          <IonImg src="assets/coin.png" alt="Price"></IonImg>{" "}
          {itinerary.cost === 0 ? t("ui.free") : itinerary.cost + " EURO"}
        </p>
        <p className="itineraryCardFooter">
          <IonImg src="assets/category.png" alt="Category"></IonImg>
          {itinerary.tags
            .map((tag) => tags[tag.id]?.locales?.[0]?.name || "")
            .filter(Boolean)
            .join(", ")}
        </p>
        <p className="itineraryCardFooter">
          <IonImg src="assets/clock.png" alt="Duration"></IonImg>
          {parseInt(itinerary.duration.split(".")[0]) === 1
            ? itinerary.duration.split(".")[0] + " " + t("ui.hour")
            : itinerary.duration.split(".")[0] + " " + t("ui.hours")}
          {itinerary.duration.split(".")[1] !== undefined &&
            " " + itinerary.duration.split(".")[1] + " " + t("ui.minutes")}
        </p>
        {userItineraryTokens.find(
          (token) => token.itinerary.id === itinerary.id
        ) ? (
          <MyButtonWithBorder
            className={`${styles.itinerariesListItemButton} ion-text-uppercase`}
            onClick={() =>
              playItinerary(
                userItineraryTokens.find(
                  (token) => token.itinerary.id === itinerary.id
                )!
              )
            }
            size="default"
            color="primary"
          >
            {t("ui.play")}
          </MyButtonWithBorder>
        ) : userContext.user.status === UserStatus.LOGGED ? (
          <MyButtonWithBorder
            className={`${styles.itinerariesListItemButton} ion-text-uppercase`}
            onClick={() => itineraryButtonClick(itinerary)}
            size="default"
            color={itinerary.cost === 0 ? "tertiary" : "secondary"}
          >
            {itinerary.cost === 0 ? t("ui.unlock") : t("ui.purchase")}
          </MyButtonWithBorder>
        ) : (
          <MyButtonWithBorder
            className={`${styles.itinerariesListItemButton} ion-text-uppercase`}
            onClick={() => history.push(Routes.ACCESS + "?city=" + itinerary.city + "&itinerary=" + createSlug(itinerary.locales[0].name))}
            size="default"
            color="secondary"
          >
            {t("ui.needLogin")}
          </MyButtonWithBorder>
        )}
      </div>
    );
  });

  async function fetchItineraries() {
    setFetchError(false);
    setPending(true);
    setIsTagsLoading(true);
    let city = selectionContext.selectedCity;
    let selectedItinerary = "";
    location.search = location.search.replace("#", "?");
    let checkIfThereIsQuery = new URLSearchParams(location.search);
    let checkIfThereIsCity = checkIfThereIsQuery.get("city");
    if (checkIfThereIsCity) {
      city = checkIfThereIsCity;
    }
    let checkIfThereIsItinerary = checkIfThereIsQuery.get("itinerary");
    if (checkIfThereIsItinerary) {
      // Extract only the slug part (before the first space that indicates share text)
      // Slugs don't contain spaces, so split by space and take the first part
      selectedItinerary = checkIfThereIsItinerary.split(" ")[0];
    }
    console.log("City: ", city);
    console.log("Itinerary: ", selectedItinerary);
    if (!city) {
      history.push(Routes.CITY_SELECTION);
      return;
    }
    const filters = {
      city,
      published: true,
    };
    try {
      // If we have a selected itinerary, fetch with both locales to ensure we can match
      // against both Italian and English names
      const currentLocale = localizationContext.locale ?? localizationContext.defaultLocale;
      const otherLocale = currentLocale === "it" ? "en" : "it";
      
      // Fetch with current locale
      const resCurrent = await get<GetItinerariesByCityOutput>({
        endpoint: `${ENDPOINT_GET_ITINERARIES_FILTERED}/${encodeURIComponent(
          JSON.stringify(filters)
        )}?locale=${currentLocale}`,
      });
      
      // If we have a selected itinerary, also fetch with the other locale to get all locale data
      let resOther: GetItinerariesByCityOutput = [];
      if (selectedItinerary !== "") {
        try {
          resOther = await get<GetItinerariesByCityOutput>({
            endpoint: `${ENDPOINT_GET_ITINERARIES_FILTERED}/${encodeURIComponent(
              JSON.stringify(filters)
            )}?locale=${otherLocale}`,
          });
        } catch (err) {
          console.log("Failed to fetch with other locale, continuing with current locale only");
        }
      }
      
      // Merge itineraries: combine locales from both responses
      const itineraryMap = new Map<number, Itinerary>();
      resCurrent.forEach((itinerary) => {
        itineraryMap.set(itinerary.id, { ...itinerary });
      });
      resOther.forEach((itinerary) => {
        const existing = itineraryMap.get(itinerary.id);
        if (existing) {
          // Merge locales: add locales from other response that don't exist in current
          const existingLocaleCodes = new Set(existing.locales.map(l => l.locale));
          itinerary.locales.forEach((locale) => {
            if (!existingLocaleCodes.has(locale.locale)) {
              existing.locales.push(locale);
            }
          });
        } else {
          // Itinerary doesn't exist in current locale, add it anyway
          itineraryMap.set(itinerary.id, { ...itinerary });
        }
      });
      const res = Array.from(itineraryMap.values());
      
      const tagsRes = await get<GetTagsByCityOutput>({
        endpoint: `${ENDPOINT_GET_TAGS_BY_CITY}/${encodeURIComponent(
          city
        )}?locale=${currentLocale}`,
      });

      // Create tags map first
      const newTags: TagsMap = {};
      for (const tag of tagsRes) {
        newTags[tag.id] = tag;
      }
      setTags(newTags);
      setIsTagsLoading(false);

      setItineraries(
        res
          .filter((itinerary) => {
            if (!ON_DEV) {
              if (itinerary.locales[0].name.indexOf("BOZZA") !== -1) {
                return false;
              }
            }
            if (selectedItinerary !== "") {
              const selectedSlug = createSlug(selectedItinerary);
              // Check if any locale matches the selected slug (check all locales: Italian, English, etc.)
              const matches = itinerary.locales.some((locale) => {
                const itinerarySlug = createSlug(locale.name);
                return itinerarySlug === selectedSlug;
              });
              if (!matches) {
                return false;
              }
            }
            return itinerary.locales.length > 0;
          })
          .map((itinerary) => {
            return {
              ...itinerary,
              locales: [
                {
                  ...itinerary.locales[0],
                  description:
                    itinerary.locales[0].description.split(" --- ")[0],
                },
              ],
              duration:
                itinerary.locales[0].description.split(" --- ")[1] ?? "1",
            };
          })
      );
    } catch (err) {
      console.log(err);
      setFetchError(true);
    }
    setPending(false);
  }

  async function fetchUserItineraries() {
    try {
      const itineraryTokens = await get<ItineraryToken[]>({
        endpoint: `${ENDPOINT_GET_ITINERARY_TOKENS_WITH_ITINERARY}/${userContext.user.id}`,
        bearerToken: userContext.user.token,
      });
      setUserItineraryTokens(itineraryTokens);
      console.log("User itineraries: ", itineraryTokens);
    } catch (err) {
      console.log("error while fetching user itineraries: ", err);
    }
  }

  return (
    <IonPage>
      <IonContent>
        <div className={styles.viewContainer}>
          {isPending ? (
            <IonSpinner />
          ) : fetchError ? (
            <>
              <p>{t("itinerarySelection.fetchError")}</p>
              <MyButtonWithBorder
                onClick={() => fetchItineraries()}
                size="default"
                color="secondary"
              >
                {t("itinerarySelection.fetch")}
              </MyButtonWithBorder>
            </>
          ) : (
            <>
              {/* <p className={`${styles.upperDescription} ion-text-uppercase`}>
                {t("itinerarySelection.chooseItinerary")}:
              </p> */}
              <IonList className={styles.itinerariesList}>
                {itineraryItems}
              </IonList>
              {/* <Virtuoso style={{height: '100%', width: '100%'}} totalCount={itineraryItems.length} itemContent={(index) => {
                            return (
                                <div key={index}>
                                {
                                    itineraryItems[index]
                                }
                                </div>
                            )
                        }}>
                        </Virtuoso> */}
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};
export default CityItinerariesPage;
